/* eslint-disable max-lines -- Pre-existing violation */
import {isFeatureEnabled} from 'feature-flags';
import {
  Action,
  Alert,
  Button,
  ButtonGroup,
  type ButtonProps,
  Card,
  closeCurrentDialog,
  DataStatus,
  Dialog,
  Dropdown,
  DropdownItem,
  EmptyStatus,
  getFilePreviewUrl,
  openDialog,
  TabProps,
  Tabs,
} from 'platform/components';
import {Box, Hide, HStack, Show, Space, VStack} from 'platform/foundation';

import {useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';

import {head, isNil, isNotNil, last, pipe} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  chatAgentApi,
  tenantApi,
  useGetParticipationQuery,
  useSubscribeToChatConversationMutation,
} from '@dms/api/core';
import {useGetCustomerV2Query} from '@dms/api/customer';
import {BusinessCaseResponseBody, OfferResponseBody} from '@dms/api/finance';
import {
  useCreateSaleOfferDocumentMutation,
  useDeleteOfferSaleVehicleMutation,
  useGetBusinessCaseQuery,
  useGetVehicleQuery,
  useLazyGetBusinessCaseQuery,
  useMarkOfferDocumentsAsActualMutation,
} from '@dms/api/sales';
import {EntityResourceIds, FileResponseBody} from '@dms/api/shared';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import {
  BusinessCaseConfigurationsCard,
  getNewVehicleDeliveryFlag,
  isNewVehicleOrderCancelled,
  SelectConfigurationDialog,
} from '@dms/features/new-vehicle';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {
  DocumentsDialog,
  getBusinessCaseTradeTypeFlag,
  getVehicleTradeType,
  handleApiError,
  queryParams,
  useBusinessCaseActions,
  usePermissions,
  usePricePermissions,
  useBcToOnlineOffer,
  useDocumentTemplates,
} from '@dms/shared';
import {selectActiveBranch, useRouter, VehicleWidget} from '@dms/teas';

import {
  buildArray,
  composePath,
  useBoolean,
  useQueryState,
  useRequiredParams,
  useToggle,
  useNavigate,
} from 'shared/web';

import {useSelectVehicleContext} from '../../../hooks/useSelectVehicleContext';
import {getOnlineOfferEngagementSummary} from '../../../utils/getOnlineOfferEngagementSummary';
import {BusinessCaseMain} from '../../BusinessCaseMain/BusinessCaseMain';
import {ChangeVehicleDialog} from '../../ChangeVehicleDialog';
import {BusinessCaseFinancialProductPreference} from './BusinessCaseFinancialProductPreference/BusinessCaseFinancialProductPreference';
import {BusinessCaseSaleVehicleComments} from './BusinessCaseSaleVehicleComments';
import {BusinessCaseSaleVehicleDiscount} from './BusinessCaseSaleVehicleDiscount/BusinessCaseSaleVehicleDiscount';
import {BusinessCaseSaleVehiclePriceSummary} from './BusinessCaseSaleVehiclePriceSummary/BusinessCaseSaleVehiclePriceSummary';
import {AlternativeTermsPanel} from './OfferAlternatives/AlternativeTermsPanel';
import {AppliedAlternativeNotes} from './OfferAlternatives/AppliedAlternativeNotes';
import {CustomerChoiceBanner} from './OfferAlternatives/CustomerChoiceBanner';
import {getAlternativeFlags} from './OfferAlternatives/getAlternativeFlags';
import {OfferAlternativesStrip} from './OfferAlternatives/OfferAlternativesStrip';
import {useOfferAlternativeActions} from './OfferAlternatives/useOfferAlternativeActions';
import {useOfferAlternativesSelling} from './OfferAlternatives/useOfferAlternativesSelling';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

const TAB_IDS = {
  VEHICLE_SELLING: 'vehicleSelling',
  VEHICLE_COMMENTS: 'vehicleComments',
};

// eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
export const BusinessCaseSaleVehicles = () => {
  const router = useRouter();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {id: businessCaseId} = useRequiredParams();
  const [activeTabId, setActiveTabId] = useQueryState(queryParams.BUSINESS_CASE_SALE_VEHICLE);

  const isFirstTabActive = activeTabId === TAB_IDS.VEHICLE_SELLING;
  const isLastTabActive = activeTabId === TAB_IDS.VEHICLE_COMMENTS;

  const activeBranch = useSelector(selectActiveBranch);
  tenantApi.endpoints.getBranch.useQuery({branchId: activeBranch});

  const {data: businessCaseParticipation} = useGetParticipationQuery({
    recordId: businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });

  const {data: businessCaseData, isLoading, isError} = useGetBusinessCaseQuery({businessCaseId});

  const [getBusinessCase] = useLazyGetBusinessCaseQuery();

  const {data: customerData} = useGetCustomerV2Query(
    {customerId: businessCaseData?.customerId ?? ''},
    {skip: isNilOrEmpty(businessCaseData?.customerId)}
  );

  const offer = businessCaseData?.offers?.[0];

  const saleVehicleData = businessCaseData?.offers?.[0]?.saleVehicles?.[0];

  const {
    data: vehicleDetail,
    isLoading: isLoadingVehicle,
    isError: isErrorVehicle,
  } = useGetVehicleQuery(
    {vehicleId: saleVehicleData?.vehicleId ?? ''},
    {skip: isNilOrEmpty(saleVehicleData?.vehicleId)}
  );

  const {data: saleVehicle} = useGetSaleVehicleQuery(
    {vehicleId: saleVehicleData?.vehicleId ?? ''},
    {skip: isNilOrEmpty(saleVehicleData?.vehicleId)}
  );

  const [
    canViewBusinessCaseSelling,
    canChangeBusinessCaseSellingVehicle,
    canRemoveBusinessCaseSellingVehicle,
    canReadSaleOfferDocument,
    canReadNewVehicleConfiguration,
    canWriteNewVehicleConfiguration,
    canOrderNewVehicleConfiguration,
    canAddVehicleToSellPermission,
    canEditSaleDiscount,
    canRemoveUpsellItem,
    canAddUpsellItemFromCatalogue,
    canAddUpsell,
    canEditUpsell,
  ] = usePermissions({
    permissionKeys: [
      'viewBusinessCaseSelling',
      'changeBusinessCaseSellingVehicle',
      'removeBusinessCaseSellingVehicle',
      'readSaleOfferDocument',
      'newVehicleConfigurationRead',
      'newVehicleConfigurationWrite',
      'newVehicleConfigurationOrder',
      'addVehicleToSell',
      'businessCaseEditSaleDiscount',
      'businessCaseRemoveUpsellItem',
      'businessCaseAddUpsellItemFromCatalogue',
      'businessCaseAddUpsell',
      'businessCaseEditUpsell',
    ],
    scopes: {
      viewBusinessCaseSelling: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      changeBusinessCaseSellingVehicle: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      removeBusinessCaseSellingVehicle: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      readSaleOfferDocument: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      businessCaseEditSaleDiscount: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      businessCaseRemoveUpsellItem: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      businessCaseAddUpsellItemFromCatalogue: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      businessCaseAddUpsell: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
      businessCaseEditUpsell: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
    },
  });

  const allowedActions = businessCaseData?.actions;

  const [id] = router.params.dynamic;
  const isDraft = id === 'concept';

  const [isLoadingRemoveVehicle, setIsLoadingRemoveVehicle, stopIsLoadingRemoveVehicle] =
    useBoolean();
  const [isConfirmRemoveVehicleOpen, toggleIsConfirmRemoveVehicleOpen] = useToggle();
  const [isConfirmChangeVehicleOpen, toggleIsConfirmChangeVehicleOpen] = useToggle();
  const [isSelectConfigurationOpen, setIsSelectConfigurationOpen] = useState(false);

  const isVehicleCommentsEnabled = isFeatureEnabled(featureFlags.SALES_SALE_VEHICLE_COMMENTS);
  // Mirrors the gate inside BusinessCaseConfigurationsCard, which renders nothing without both.
  const hasNewVehiclePath =
    isFeatureEnabled(featureFlags.NEW_VEHICLE_CATALOGUE) && Boolean(canReadNewVehicleConfiguration);

  const isVehicleFinancingEnabled = isFeatureEnabled(featureFlags.VEHICLE_FINANCING);

  // The backend requires the ADD_VEHICLE_TO_SELL permission to add an alternative or apply a stock one.
  const canAddVehicleToSell =
    Boolean(canViewBusinessCaseSelling) && Boolean(canAddVehicleToSellPermission);
  // Same rights as the deal vehicle's controls: a typed upsell line sets a price and needs
  // ADD_UPSELL (edit upsell prices); a catalogue line is priced by the backend and needs
  // ADD_UPSELL_ITEM_FROM_CATALOGUE (manage upsell).
  const offerAlternativesSelling = useOfferAlternativesSelling(businessCaseId, {
    canAddVehicleToSell,
    canChangeSaleVehicle: Boolean(canChangeBusinessCaseSellingVehicle),
    canRemoveSaleVehicle: Boolean(canRemoveBusinessCaseSellingVehicle),
    canOrderConfiguration: Boolean(canOrderNewVehicleConfiguration),
    canEditDiscount: Boolean(canEditSaleDiscount),
    canAddUpsell: Boolean(canAddUpsell),
    canAddUpsellFromCatalogue: Boolean(canAddUpsellItemFromCatalogue),
    canEditUpsell: Boolean(canAddUpsell) || Boolean(canEditUpsell),
    canRemoveUpsell: Boolean(canRemoveUpsellItem),
    hasSaleVehicle: isNotNil(saleVehicleData),
  });
  const offerAlternativeActions = useOfferAlternativeActions(businessCaseId);
  const activeAlternative = offerAlternativesSelling.activeAlternative;
  // The applied alternative is the deal vehicle and keeps the existing UI; others get the panel.
  const panelAlternative =
    offerAlternativesSelling.hasAlternatives && activeAlternative && !activeAlternative.isApplied
      ? activeAlternative
      : null;
  const appliedAlternative = offerAlternativesSelling.alternatives.find(
    (alternative) => alternative.isApplied
  );
  const customerChoice = offerAlternativesSelling.offerAlternatives?.customerChoice;
  const currentlyChosenAlternative = customerChoice?.isCurrent
    ? offerAlternativesSelling.alternatives.find(
        (alternative) => alternative.id === customerChoice.alternativeId
      )
    : undefined;
  // Without alternatives yet, the first one is added from the vehicle menu or the empty slot.
  const canAddFirstAlternative =
    offerAlternativesSelling.canAdd && !offerAlternativesSelling.hasAlternatives;
  const addAlternative = () =>
    offerAlternativeActions.add((alternativeId) =>
      offerAlternativesSelling.setActiveId(alternativeId)
    );

  const {setSelectSaleVehicleOpen} = useSelectVehicleContext();
  const [getTemplatesByCode] = useDocumentTemplates();
  const {getDropdownComponent} = getTemplatesByCode('sale-offer');

  const [deleteOfferSaleVehicle] = useDeleteOfferSaleVehicleMutation();
  const [markDocumentsAsActual] = useMarkOfferDocumentsAsActualMutation();
  const [subscribeToChatConversation] = useSubscribeToChatConversationMutation();

  const [createSaleOfferDocument, {isLoading: isCreateSaleOfferDocumentLoading}] =
    useCreateSaleOfferDocumentMutation();

  const {isActionEnabled} = useBusinessCaseActions(id);
  const grossVehiclePrice =
    saleVehicleData?.priceTotalAfterDiscount?.withVat ?? saleVehicleData?.sellingPrice?.withVat;
  const financialProductVehiclePrice = isNil(grossVehiclePrice)
    ? null
    : {amount: Number(grossVehiclePrice.amount), currency: grossVehiclePrice.currency};

  // With alternatives the deal vehicle is swapped by applying another alternative; the legacy
  // change-vehicle routes would bypass it. Removing the vehicle stays: it is a clean un-apply.
  const canSwitchSaleVehicle =
    isActionEnabled('SWITCH_OFFER_SALE_VEHICLE') &&
    Boolean(canChangeBusinessCaseSellingVehicle) &&
    !offerAlternativesSelling.hasAlternatives;

  const {
    canSendOffer,
    canResendOffer,
    canInvalidateOffer,
    isOnlineOfferSent,
    isOnlineOfferCreated,
    actions,
    canOpenOnlineOffer,
    canReadOnlineOffer,
    isOpenOnlineOfferLoading,
    offerValidity,
  } = useBcToOnlineOffer(businessCaseId);

  const engagementSummary = getOnlineOfferEngagementSummary(
    businessCaseData?.onlineOfferEngagement
  );

  const handleChatInvalidation = () => {
    setTimeout(async () => {
      await getBusinessCase({businessCaseId})
        .unwrap()
        .then(async (data) => {
          const newActiveConversation = data?.chatConversations?.find((conv) => conv.isActive);

          if (newActiveConversation?.conversationId) {
            try {
              await subscribeToChatConversation({
                conversationId: newActiveConversation.conversationId,
              }).unwrap();
            } catch (error) {
              console.error('Failed to subscribe to chat conversation:', error);
            }
          }

          dispatch(chatAgentApi.util.invalidateTags(['ConversationEvents', 'Conversation']));
        });
    }, 5000);
  };

  const {
    canReadBusinessCaseSaleVehicleSalePrice,
    canReadBusinessCaseSaleTotalPrice,
    canViewAnyOfSalePriceFields,
    canViewAnyOfPurchasePriceFields,
  } = usePricePermissions({businessCaseRecordId: businessCaseId, vehicleRecordId: null});

  const canReadPrices = canViewAnyOfSalePriceFields || canViewAnyOfPurchasePriceFields;

  const handleRemoveVehicle = async () => {
    if (!(offer && businessCaseData && saleVehicleData)) {
      return;
    }

    setIsLoadingRemoveVehicle();

    await deleteOfferSaleVehicle({
      businessCaseId: businessCaseData.id,
      offerId: offer.id,
      offerSaleVehicleId: saleVehicleData.vehicleId,
    })
      .unwrap()
      .catch(handleApiError);

    stopIsLoadingRemoveVehicle();
    toggleIsConfirmRemoveVehicleOpen();
  };

  const handleMarkOfferDocumentsAsActual = () => {
    if (!(offer && businessCaseData && saleVehicleData)) {
      return;
    }

    markDocumentsAsActual({
      businessCaseId: businessCaseData.id,
      offerId: offer.id,
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleCreateDocuments = (templateId: string) =>
    createSaleOfferDocument({
      saleOfferRequestBody: {businessCaseId, templateId},
    })
      .unwrap()
      .then((businessCase: BusinessCaseResponseBody) => {
        const offerFile = pipe(
          (businessCase: BusinessCaseResponseBody) => head(businessCase?.offers),
          (offer: OfferResponseBody | undefined) => offer?.files,
          (files) => last(files ?? [])
        )(businessCase);

        if (isNil(offerFile)) {
          return;
        }

        const document: FileResponseBody = {
          id: offerFile.fileFileId ?? '',
          filename: offerFile.name,
          fileUri: offerFile.fileUri,
          pdfFilename: offerFile.name + '.pdf',
          pdfUri: getFilePreviewUrl(offerFile.fileUri),
        };

        openDialog(
          <DocumentsDialog
            customerId={businessCase.customerId}
            recordId={businessCase.id}
            resourceId={EntityResourceIds.businessCase}
            documents={[document]}
            permissions={{
              canReadSaleOfferDocument,
            }}
            text={i18n.t('page.businessCase.labels.offerForCustomerNote')}
          />,
          {
            title: i18n.t('page.businessCase.labels.offerForCustomer'),
            buttons: [
              {
                variant: 'secondary',
                title: i18n.t('general.actions.close'),
                onClick: closeCurrentDialog,
              },
            ],
          }
        );
      })
      .catch(handleApiError);

  const setIsSelectVehicleOpen = () => {
    setSelectSaleVehicleOpen(true);
  };

  const onChangeVehicleConfirm = () => {
    toggleIsConfirmChangeVehicleOpen();
    setIsSelectVehicleOpen();
  };

  // Both new-vehicle routes commit with the observed selection, so the backend swaps the vehicle
  // in one transaction and answers 409 if the Business Case moved in the meantime. Nothing is
  // removed up front: abandoning the configurator leaves the current vehicle in place.
  const changeToNewVehicle = (continueWith: VoidFunction) => {
    toggleIsConfirmChangeVehicleOpen();
    continueWith();
  };

  // A replacing commit skips the sibling check and fences on the observed selection instead, so
  // a configuration already holding this slot no longer rules the routes out. The backend still
  // demands the change-vehicle permission for it, which is the same one that opened this menu.
  const canReplaceWithNewVehicle =
    hasNewVehiclePath &&
    Boolean(canChangeBusinessCaseSellingVehicle) &&
    ['CONCEPT', 'OFFER'].some((state) => state === businessCaseData?.businessCaseState);
  const saleVehicleReplacement = {expectedSaleVehicleId: businessCaseData?.saleVehicleId ?? null};
  const canConfigureNewVehicle =
    canReplaceWithNewVehicle && Boolean(canWriteNewVehicleConfiguration);
  const canCommitNewVehicle = canReplaceWithNewVehicle && Boolean(canOrderNewVehicleConfiguration);

  // The trade type says where the car came from, not whether it exists yet. While its order has
  // not arrived that answer is misleading, and after a cancellation it is simply false. Both
  // answers come from the sale vehicle's own arrival facts (D53), so nothing extra is fetched -
  // which also keeps the warning alive after cancellation releases the configuration.
  const deliveryFlag = getNewVehicleDeliveryFlag(saleVehicle);
  const isOrderCancelled = isNewVehicleOrderCancelled(saleVehicle);
  const tradeTypeFlag = getBusinessCaseTradeTypeFlag(
    getVehicleTradeType(saleVehicle, businessCaseData)
  );
  const flag = deliveryFlag
    ? {label: deliveryFlag.label, colorScheme: deliveryFlag.colorScheme}
    : tradeTypeFlag;

  // The offer PDF is built from the deal vehicle; with alternatives that only exists once one is
  // applied (the backend refuses otherwise).
  const isAwaitingAppliedAlternative =
    offerAlternativesSelling.hasAlternatives && isNil(appliedAlternative);

  const vehicleFlags = [
    ...(flag && flag.label && flag.colorScheme
      ? [{name: flag.label, color: flag.colorScheme}]
      : []),
    ...(appliedAlternative
      ? getAlternativeFlags(
          appliedAlternative,
          offerAlternativesSelling.offerAlternatives?.customerChoice
        )
      : []),
  ];

  const tabs: TabProps[] = buildArray<TabProps>()
    .when(canReadBusinessCaseSaleVehicleSalePrice, {
      id: TAB_IDS.VEHICLE_SELLING,
      title: i18n.t('page.businessCase.labels.typeSelling'),
      'data-testid': testIds.businessCase.selling('vehicle-selling'),
      content: <BusinessCaseSaleVehicleDiscount />,
    })
    .when(isVehicleCommentsEnabled, {
      id: TAB_IDS.VEHICLE_COMMENTS,
      title: i18n.t('entity.vehicle.labels.vehicleComments'),
      'data-testid': testIds.businessCase.selling('vehicle-comments'),
      content: <BusinessCaseSaleVehicleComments vehicleId={saleVehicleData?.vehicleId} />,
    });

  const moveToNextTab = () => {
    const activeTabIndex = tabs.findIndex((item) => item.id === activeTabId);
    const nextTabId = tabs[activeTabIndex + 1]?.id;
    if (isNilOrEmpty(nextTabId)) {
      return;
    }

    setActiveTabId(nextTabId);
  };

  const moveToPreviousTab = () => {
    const activeTabIndex = tabs.findIndex((item) => item.id === activeTabId);
    const previousTabId = tabs[activeTabIndex - 1]?.id;
    if (isNilOrEmpty(previousTabId)) {
      return;
    }

    setActiveTabId(previousTabId);
  };

  const onForward = () => {
    if (isLastTabActive || !isVehicleCommentsEnabled) {
      navigate(composePath(businessCaseRoutes.testDrive, {params: {id: businessCaseId}}));

      return;
    }

    moveToNextTab();
  };

  const onBackward = () => moveToPreviousTab();

  return (
    <BusinessCaseMain
      // With alternatives each carries its own validity; the deal's own date only means something
      // once one of them is applied (it then mirrors that alternative's validity).
      offerValidity={isAwaitingAppliedAlternative ? undefined : offerValidity}
      actions={buildArray<Action>()
        .when(canOpenOnlineOffer, {
          title: i18n.t('page.businessCase.actions.viewOnlineOffer'),
          type: 'button',
          leftIcon: 'image/remove_red_eye',
          variant: 'outlined',
          isLoading: isOpenOnlineOfferLoading,
          isDisabled: !canReadOnlineOffer,
          onClick: actions.openOffer,
        })
        .when(canSendOffer, {
          title: i18n.t('page.businessCase.actions.sendOnlineOffer'),
          type: 'button',
          leftIcon: 'content/mail',
          variant: 'outlined',
          onClick: () =>
            actions.sendOnlineOfferToCustomer({
              shouldCreateOffer: true,
              onSuccess: handleChatInvalidation,
            }),
        })
        .when(canResendOffer, {
          title: i18n.t('page.businessCase.actions.resendOnlineOffer'),
          type: 'button',
          leftIcon: 'communication/mark_email_read',
          variant: 'success',
          onClick: () =>
            actions.sendOnlineOfferToCustomer({
              shouldCreateOffer: false,
              onSuccess: handleChatInvalidation,
            }),
        })
        .when(canInvalidateOffer, {
          title: i18n.t('page.businessCase.actions.discardOnlineOffer'),
          type: 'button',
          leftIcon: 'notification/do_disturb',
          variant: 'outlined',
          onClick: actions.invalidateOnlineOfferToCustomer,
        })
        .when(isActionEnabled('GENERATE_SALE_OFFER_DOCUMENT') && !isAwaitingAppliedAlternative, {
          type: 'custom',
          component: getDropdownComponent({
            onChange: handleCreateDocuments,
            variant: 'outlined',
            isDisabled: isCreateSaleOfferDocumentLoading,
            title: i18n.t('page.businessCase.actions.generateOffer'),
            customerPreferredLocale: customerData?.preferredLocale,
            'data-testid': testIds.businessCase.selling('generateOfferDropdown'),
          }),
        })}
    >
      <Show when={offer?.documentsIsActual === false}>
        <Alert
          title={i18n.t('page.businessCase.notifications.checkSaleVehicleDocuments')}
          variant="warning"
          onClose={handleMarkOfferDocumentsAsActual}
          data-testid={testIds.businessCase.selling('checkSaleVehicleDocumentsAlert')}
        />
        <Space vertical={4} />
      </Show>
      <DataStatus isLoading={isLoading || isLoadingVehicle} isError={isError || isErrorVehicle}>
        <Show
          when={
            offerAlternativesSelling.hasAlternatives && offerAlternativesSelling.offerAlternatives
          }
        >
          {(offerAlternatives) => (
            <VStack spacing={3}>
              <CustomerChoiceBanner
                businessCaseId={businessCaseId}
                offerAlternatives={offerAlternatives}
                isApplying={offerAlternativeActions.isApplying}
                onApply={offerAlternativeActions.apply}
              />
              <OfferAlternativesStrip
                offerAlternatives={offerAlternatives}
                alternatives={offerAlternativesSelling.alternatives}
                activeAlternativeId={activeAlternative?.id ?? ''}
                canReadTotalPrice={canReadBusinessCaseSaleTotalPrice}
                onSelect={offerAlternativesSelling.setActiveId}
                onAdd={addAlternative}
              />
              <Space vertical={1} />
            </VStack>
          )}
        </Show>
        <Show when={panelAlternative}>
          {(alternative) => (
            <AlternativeTermsPanel
              // A new source is a new vehicle: no edit typed against the old one may survive it.
              key={`${alternative.id}:${alternative.configurationId}:${alternative.vehicleId}`}
              businessCaseId={businessCaseId}
              alternative={alternative}
              customerChoice={offerAlternativesSelling.offerAlternatives?.customerChoice}
              chosenAlternative={currentlyChosenAlternative}
            />
          )}
        </Show>
        <Hide when={isNotNil(panelAlternative)}>
          {saleVehicleData && vehicleDetail ? (
            <Card>
              {/* The configuration is released from the Business Case on cancellation, so the New
                vehicle card below is gone and this is the only place left that can say the car
                is not coming. Carries the way out, because the offer cannot be completed. */}
              <Show when={isOrderCancelled}>
                <Box paddingBottom={3}>
                  <Alert
                    variant="warning"
                    layout="detailed"
                    title={t(
                      'page.businessCase.notifications.newVehicleOrderCancelledTitle',
                      'Ordered vehicle cancelled'
                    )}
                    message={t(
                      'page.businessCase.notifications.newVehicleOrderCancelled',
                      'This vehicle will not be delivered and the final invoice cannot be issued for it. Sell another vehicle instead, or close the business case as unsuccessful.'
                    )}
                    actions={buildArray<ButtonProps>()
                      .when(canSwitchSaleVehicle, {
                        variant: 'primary',
                        title: i18n.t('page.businessCase.actions.changeVehicle'),
                        onClick: toggleIsConfirmChangeVehicleOpen,
                      })
                      .when(
                        isActionEnabled('DELETE_OFFER_SALE_VEHICLE') &&
                          canRemoveBusinessCaseSellingVehicle,
                        {
                          variant: 'secondary',
                          title: i18n.t('page.businessCase.actions.removeVehicle'),
                          onClick: toggleIsConfirmRemoveVehicleOpen,
                        }
                      )}
                    data-testid={testIds.businessCase.selling('newVehicleOrderCancelled')}
                  />
                </Box>
              </Show>
              <Show when={isOnlineOfferSent && isOnlineOfferCreated}>
                <Box paddingBottom={3}>
                  <Alert
                    title={i18n.t('page.businessCase.notifications.onlineOfferSent')}
                    secondaryMessage={engagementSummary ?? undefined}
                    layout="detailed"
                    variant="success"
                    type="inline"
                    data-testid={testIds.businessCase.selling('onlineOfferSent')}
                  />
                </Box>
              </Show>
              <Box paddingBottom={4}>
                <VehicleWidget
                  data-testid={testIds.businessCase.selling('vehicle')}
                  vehicle={vehicleDetail}
                  selling={saleVehicleData}
                  shouldShowPrice={canViewAnyOfSalePriceFields}
                  showMileageSale
                  saleVehicleId={businessCaseData?.saleVehicleId ?? ''}
                  extraControls={
                    <Show
                      when={
                        canSwitchSaleVehicle ||
                        (isActionEnabled('DELETE_OFFER_SALE_VEHICLE') &&
                          canRemoveBusinessCaseSellingVehicle) ||
                        canAddFirstAlternative
                      }
                    >
                      <Dropdown
                        dropdownControl={
                          <Button
                            variant="outlined"
                            leftIcon="navigation/more_vert"
                            title={i18n.t('page.businessCase.labels.actions')}
                            data-testid={testIds.businessCase.selling('dropdownControlButton')}
                          />
                        }
                        data-testid={testIds.businessCase.selling('dropdown')}
                      >
                        <Show when={canSwitchSaleVehicle}>
                          <DropdownItem
                            label={i18n.t('page.businessCase.actions.changeVehicle')}
                            leftIcon="navigation/refresh"
                            onClick={toggleIsConfirmChangeVehicleOpen}
                          />
                        </Show>
                        <Show when={canAddFirstAlternative}>
                          <DropdownItem
                            label={t('entity.offerAlternative.actions.add', 'Add alternative')}
                            leftIcon="content/add_circle"
                            onClick={addAlternative}
                          />
                        </Show>
                        <Show
                          when={
                            isActionEnabled('DELETE_OFFER_SALE_VEHICLE') &&
                            canRemoveBusinessCaseSellingVehicle
                          }
                        >
                          <DropdownItem
                            severity="danger"
                            label={i18n.t('page.businessCase.actions.removeVehicle')}
                            leftIcon="action/delete"
                            onClick={toggleIsConfirmRemoveVehicleOpen}
                          />
                        </Show>
                      </Dropdown>
                    </Show>
                  }
                  flags={vehicleFlags.length > 0 ? vehicleFlags : undefined}
                />

                <ChangeVehicleDialog
                  isConfirmChangeVehicleOpen={isConfirmChangeVehicleOpen}
                  toggleIsConfirmChangeVehicleOpen={toggleIsConfirmChangeVehicleOpen}
                  onChangeVehicleConfirm={onChangeVehicleConfirm}
                  onConfigureNewVehicle={
                    canConfigureNewVehicle
                      ? () =>
                          changeToNewVehicle(() =>
                            navigate(
                              composePath(vehiclesRoutes.newVehicleConfigurator, {
                                params: {step: '1'},
                                queryParams: {
                                  businessCaseId,
                                  replaceSaleVehicleId: businessCaseData?.saleVehicleId,
                                },
                              })
                            )
                          )
                      : undefined
                  }
                  onUseExistingConfiguration={
                    canCommitNewVehicle
                      ? () => changeToNewVehicle(() => setIsSelectConfigurationOpen(true))
                      : undefined
                  }
                />

                <Show when={isSelectConfigurationOpen}>
                  <SelectConfigurationDialog
                    businessCaseId={businessCaseId}
                    isOpen={isSelectConfigurationOpen}
                    replacement={saleVehicleReplacement}
                    onClose={() => setIsSelectConfigurationOpen(false)}
                    // Committing invalidates only the new-vehicle tags, so the Business Case that
                    // just gained the vehicle has to be pulled again for the swap to show.
                    onDone={() => void getBusinessCase({businessCaseId})}
                  />
                </Show>

                <Dialog
                  data-testid={testIds.businessCase.selling('remove-confirm')}
                  isOpen={isConfirmRemoveVehicleOpen}
                  onClose={toggleIsConfirmRemoveVehicleOpen}
                  size="small"
                  title={i18n.t`page.businessCase.labels.confirmRemoveVehicle`}
                >
                  <ButtonGroup align="right">
                    <Button
                      variant="secondary"
                      onClick={toggleIsConfirmRemoveVehicleOpen}
                      isDisabled={isLoadingRemoveVehicle}
                      title={i18n.t('general.labels.no')}
                    />
                    <Button
                      variant="primary"
                      onClick={handleRemoveVehicle}
                      isLoading={isLoadingRemoveVehicle}
                      title={i18n.t('general.labels.yes')}
                    />
                  </ButtonGroup>
                </Dialog>
              </Box>
              {/* Where the vehicle came from belongs with the vehicle, not below the upsell. */}
              <BusinessCaseConfigurationsCard
                businessCaseId={businessCaseId}
                businessCaseState={businessCaseData?.businessCaseState}
                hasSaleVehicle
              />
              <Show when={appliedAlternative}>
                {(alternative) => (
                  <Box paddingBottom={4}>
                    <AppliedAlternativeNotes alternative={alternative} />
                  </Box>
                )}
              </Show>
              <Show when={canReadPrices}>
                <Box paddingBottom={4}>
                  <BusinessCaseSaleVehiclePriceSummary
                    data-testid={testIds.businessCase.selling('summary')}
                    saleVehicle={saleVehicleData}
                    offerTotalPrice={businessCaseData?.offers?.[0]?.offerTotalPrice}
                    isBrokeredSale={businessCaseData?.brokerageBusinessCaseId}
                    businessCaseId={businessCaseData?.id}
                    offer={businessCaseData?.offers?.[0]}
                  />
                </Box>
              </Show>
              <Show when={isVehicleFinancingEnabled}>
                <Box paddingBottom={4}>
                  <BusinessCaseFinancialProductPreference
                    key={businessCaseId}
                    canUpdate={isActionEnabled('UPDATE_FINANCIAL_PRODUCT_PREFERENCE')}
                    vehiclePrice={financialProductVehiclePrice}
                    businessCaseId={businessCaseId}
                    data-testid={testIds.businessCase.selling('financialProducts')}
                  />
                </Box>
              </Show>
              <Box>
                <Show when={isVehicleCommentsEnabled}>
                  <Tabs
                    data-testid={testIds.businessCase.selling('vehicle')}
                    variant="condensed"
                    tabs={tabs}
                    activeTabId={activeTabId}
                    onChange={setActiveTabId}
                  />
                </Show>
                <Hide when={isVehicleCommentsEnabled}>
                  <BusinessCaseSaleVehicleDiscount />
                </Hide>
              </Box>
            </Card>
          ) : (
            <Box paddingTop={20} paddingBottom={4}>
              {/*
               * One empty slot, so one block: where the catalogue is available the card draws the
               * slot itself and offers both routes. Without it, nothing changes.
               */}
              <Show when={hasNewVehiclePath}>
                <BusinessCaseConfigurationsCard
                  businessCaseId={businessCaseId}
                  businessCaseState={businessCaseData?.businessCaseState}
                  stockAction={
                    canViewBusinessCaseSelling
                      ? {
                          onClick: setIsSelectVehicleOpen,
                          isDisabled: !isActionEnabled('ADD_VEHICLE_TO_SELL'),
                        }
                      : undefined
                  }
                />
              </Show>
              <Show when={canAddFirstAlternative}>
                <Box paddingTop={4}>
                  <HStack justify="center">
                    <Button
                      variant="link"
                      leftIcon="content/add_circle"
                      title={t(
                        'entity.offerAlternative.actions.offerAlternatives',
                        'Offer several alternatives instead'
                      )}
                      onClick={addAlternative}
                      data-testid={testIds.businessCase.selling('addFirstOfferAlternative')}
                    />
                  </HStack>
                </Box>
              </Show>
              <Hide when={hasNewVehiclePath}>
                <EmptyStatus
                  data-testid={testIds.businessCase.selling('noVehicle')}
                  headline={i18n.t('page.businessCase.notifications.noVehicleAddedToSale')}
                  action={
                    canViewBusinessCaseSelling
                      ? {
                          onClick: setIsSelectVehicleOpen,
                          title: i18n.t('entity.vehicle.actions.addVehicleToSell'),
                          isDisabled: !isActionEnabled('ADD_VEHICLE_TO_SELL'),
                          leftIcon: 'content/add_circle',
                        }
                      : undefined
                  }
                />
              </Hide>
            </Box>
          )}
        </Hide>
      </DataStatus>
      <Show when={saleVehicleData}>
        <Box paddingTop={4}>
          <HStack justify={isFirstTabActive || !activeTabId ? 'flex-end' : 'space-between'}>
            <Hide when={isFirstTabActive || !activeTabId}>
              <Button
                data-testid={testIds.businessCase.selling('back')}
                onClick={onBackward}
                variant="outlined"
                leftIcon="navigation/chevron_left"
                title={i18n.t('general.actions.back')}
              />
            </Hide>
            <Button
              data-testid={testIds.businessCase.selling('continue')}
              isDisabled={
                isLastTabActive &&
                isVehicleCommentsEnabled &&
                (isDraft || !allowedActions?.checkoutViewDetail)
              }
              onClick={onForward}
              variant="outlined"
              rightIcon="navigation/chevron_right"
              title={i18n.t('general.actions.continue')}
            />
          </HStack>
        </Box>
      </Show>
    </BusinessCaseMain>
  );
};
